import React, { useEffect, useRef, useState } from 'react';
import { Calendar, User, Package, CheckCircle, Clock, AlertTriangle, FileText, Edit } from 'lucide-react';
import { DeploymentRequest, User as UserType } from '../../types';
import { ActionType } from '../../types/deploymentTypes';
import ApprovalTimeline from './ApprovalTimeline';
import DbScriptViewer from './DbScriptViewer';
import { CATEGORICAL_PALETTE } from '../../utils/chartColors';
import Avatar from '../common/Avatar';
import { toast } from '../../utils/toast';

// Module-scope cache shared across every DeploymentCard instance. Each card
// used to independently fetch the full modules/institutes lists on its own
// mount — harmless with a handful of cards, but a list view renders one card
// per deployment with no pagination, and at real production volume (hundreds
// of records) that fanned out into hundreds of simultaneous duplicate
// requests, enough to make the whole page unresponsive. One shared fetch,
// reused by every card, regardless of how many mount at once.
let modulesCache: any[] | null = null;
let modulesFetchPromise: Promise<any[]> | null = null;
function fetchAllModulesShared(): Promise<any[]> {
  if (modulesCache) return Promise.resolve(modulesCache);
  if (!modulesFetchPromise) {
    modulesFetchPromise = fetch('/api/modules')
      .then(res => res.json())
      .then(data => { modulesCache = data; return data; })
      .catch(() => { modulesCache = []; return []; });
  }
  return modulesFetchPromise;
}

let institutesCache: any[] | null = null;
let institutesFetchPromise: Promise<any[]> | null = null;
function fetchAllInstitutesShared(): Promise<any[]> {
  if (institutesCache) return Promise.resolve(institutesCache);
  if (!institutesFetchPromise) {
    institutesFetchPromise = fetch('/api/institutes')
      .then(res => res.json())
      .then(data => { institutesCache = data; return data; })
      .catch(() => { institutesCache = []; return []; });
  }
  return institutesFetchPromise;
}

// A stack of these cards previously all looked identical (same white card,
// same gray border, same header) — the only differentiators were small badges
// buried near the bottom. This gives each card a priority-colored left accent
// + a matching number-badge color, so priority is scannable without reading
// any text, and a stable per-module color dot so the same module is
// recognizable at a glance across many cards.
const PRIORITY_ACCENT: Record<string, { border: string; chip: string; text: string }> = {
  critical: { border: 'border-l-red-500', chip: 'bg-red-100', text: 'text-red-700' },
  high: { border: 'border-l-orange-500', chip: 'bg-orange-100', text: 'text-orange-700' },
  medium: { border: 'border-l-amber-500', chip: 'bg-amber-100', text: 'text-amber-700' },
  low: { border: 'border-l-sky-500', chip: 'bg-sky-100', text: 'text-sky-700' },
};

const getPriorityAccent = (priority?: string) => PRIORITY_ACCENT[priority || ''] || {
  border: 'border-l-gray-300', chip: 'bg-slate-200', text: 'text-slate-700'
};

// Deterministic name -> categorical color, so the same module always renders
// the same color across every card (not tied to any single card's ranking).
const getModuleColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return CATEGORICAL_PALETTE[Math.abs(hash) % CATEGORICAL_PALETTE.length];
};

interface DeploymentCardProps {
  deployment: DeploymentRequest;
  currentUser: UserType;
  onAction: (action: ActionType, deploymentId: string, description?: string, releaseNote?: string) => void | Promise<void>;
  onUpdate?: (updatedDeployment: DeploymentRequest) => void;
  showActions?: boolean;
  isMinimalView?: boolean;
}

const DeploymentCard: React.FC<DeploymentCardProps> = ({
  deployment,
  currentUser,
  onAction,
  onUpdate,
  showActions = true
}) => {
  // State for DB script modal
  const [showDbScriptModal, setShowDbScriptModal] = useState(false);
  
  // State for description modal
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  
  // State for edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState<any>(null);
  const [availableTesters, setAvailableTesters] = useState<any[]>([]);
  const [availableTLs, setAvailableTLs] = useState<any[]>([]);
  const [availablePMs, setAvailablePMs] = useState<any[]>([]);
  const [availableDevops, setAvailableDevops] = useState<any[]>([]);
  const [availableModules, setAvailableModules] = useState<any[]>([]);
  const [availableInstitutes, setAvailableInstitutes] = useState<any[]>([]);
  // Optional, manually-entered branch name per module while editing (moduleId -> branch)
  const [editModuleBranches, setEditModuleBranches] = useState<Record<string, string>>({});
  
  // State for action with optional description
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState<ActionType | null>(null);
  const [actionDescription, setActionDescription] = useState('');
  // New: release note for PM approvals
  const [actionReleaseNote, setActionReleaseNote] = useState('');
  // In-flight guard: a ref (not just state) so a fast double-click can't slip
  // a second request in before the re-render that disables the button.
  const actionInFlightRef = useRef(false);
  const [submittingAction, setSubmittingAction] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const isReleaseNoteApplicable = !['db-script', 'new-deployment'].includes((deployment as any).deploymentType || '');
  
  // State for expanding minimal view
  const [isExpanded, setIsExpanded] = useState(false);
  
  // State for institute deployment tracking
  const [deployedInstitutes, setDeployedInstitutes] = useState<string[]>([]);
  const [togglingInstitute, setTogglingInstitute] = useState(false);
  const [notifyingProgress, setNotifyingProgress] = useState(false);
  
  // Initialize deployed institutes from deployment data
  useEffect(() => {
    if (deployment.deployment && deployment.deployment.deployedInstitutes) {
      setDeployedInstitutes(deployment.deployment.deployedInstitutes.map((inst: any) => inst.instituteId));
    }
  }, [deployment]);
  
  // State for all modules and institutes
  const [allModules, setAllModules] = useState<any[]>([]);
  const [allInstitutes, setAllInstitutes] = useState<any[]>([]);

  // Handler to open action modal
  const handleActionClick = (action: ActionType) => {
    setActionType(action);
    setActionDescription('');
    setActionReleaseNote('');
    setShowActionModal(true);
  };

  // State for all modules and institutes


  useEffect(() => {
    let cancelled = false;
    fetchAllModulesShared().then(data => { if (!cancelled) setAllModules(data); });
    fetchAllInstitutesShared().then(data => { if (!cancelled) setAllInstitutes(data); });
    return () => { cancelled = true; };
  }, []);

  // Helper to map module IDs/objects to display names, keeping each module's
  // optional manually-entered branch alongside its name
  // Accepts array of IDs/strings or array of objects with _id/name/branch
  const getModulesWithBranch = (modules: any[] | undefined, fallback?: string): Array<{ name: string; branch?: string }> => {
    if (!modules || modules.length === 0) {
      if (fallback) {
        const found = allModules.find(m => m._id === fallback || m.id === fallback || m.name === fallback);
        return [{ name: found ? found.name : fallback }];
      }
      return [];
    }
    return modules.map(mod => {
      if (mod && typeof mod === 'object') {
        return { name: mod.name || mod._id, branch: mod.branch || undefined };
      }
      const found = allModules.find(m => m._id === mod || m.id === mod || m.name === mod);
      return { name: found ? found.name : mod };
    });
  };

  const getInstituteNames = (institutes: any[] | undefined, fallback?: string) => {
    if (!institutes || institutes.length === 0) {
      if (fallback) {
        const found = allInstitutes.find(i => i._id === fallback || i.id === fallback || i.name === fallback);
        return found ? found.name : fallback;
      }
      return '—';
    }
    const names: string[] = [];
    for (const inst of institutes) {
      if (inst && typeof inst === 'object') {
        if (inst.name) names.push(inst.name);
        else if (inst._id) names.push(inst._id);
      } else if (typeof inst === 'string') {
        const found = allInstitutes.find(i => i._id === inst || i.id === inst || i.name === inst);
        names.push(found ? found.name : inst);
      }
    }
    return names.length > 0 ? names.join(', ') : '—';
  };

  const getStatusBadgeColor = (status: string | undefined) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'tl_approved': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pm_approved': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'deployed': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'rolled_back': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string | undefined) => {
    // A deployment that's already live via emergency-deploy still shows its
    // real approval-stage text underneath, but the card's main badge should
    // make clear it's already in production — see isEmergencyLive below.
    switch (status) {
      case 'pending': return 'TL Pending';
      case 'tl_approved': return 'PM Pending';
      case 'pm_approved': return 'Ready to Deploy';
      case 'deployed': return 'Deployed';
      case 'rejected': return 'Rejected';
      case 'rolled_back': return 'Rolled Back';
      default: return 'Unknown';
    }
  };

  // A rolled-back emergency deploy is no longer live, even though the flag stays set
  const isEmergencyLive = deployment.status !== 'deployed' && deployment.status !== 'rejected' && (deployment.status as string) !== 'rolled_back' && !!deployment.deployment?.isEmergency;

  const isAdmin = currentUser.role === 'admin';
  const isOpenStatus = !['deployed', 'rejected', 'rolled_back'].includes(deployment.status as string);
  const isMe = (p?: { id?: string; email?: string } | null) =>
    !!p && ((!!p.id && p.id === currentUser.id) || (!!p.email && !!currentUser.email && p.email === currentUser.email));
  // A TL/PM may only act on a request they're actually listed on, and only
  // until they've acted — the backend 400s anyone else ("TL not found in
  // approvals list"), so showing them the buttons was just a trap.
  const myTlApproval = Array.isArray(deployment.tlApprovals) ? deployment.tlApprovals.find((a: any) => isMe(a?.tl)) : undefined;
  const myPmApproval = Array.isArray(deployment.pmApprovals) ? deployment.pmApprovals.find((a: any) => isMe(a?.pm)) : undefined;
  const tlCanAct = currentUser.role === 'tl' && deployment.status === 'pending' && !!myTlApproval && !myTlApproval.approved;
  const pmCanAct = currentUser.role === 'pm' && deployment.status === 'tl_approved' && !!myPmApproval && !myPmApproval.approved;

  // Admin mirrors server.cjs requireRole: /approve/tl + /approve/pm allow admin,
  // /deploy + /emergency-deploy ('devops','admin'), /reject ('tl','pm','devops','admin').
  const canApprove = () => {
    return tlCanAct || pmCanAct ||
      (isAdmin && (deployment.status === 'pending' || deployment.status === 'tl_approved'));
  };

  const canDeploy = () => {
    return (currentUser.role === 'devops' || isAdmin) && deployment.status === 'pm_approved';
  };

  const canEmergencyDeploy = () => {
    return (currentUser.role === 'devops' || isAdmin) && isOpenStatus && !deployment.deployment?.isEmergency;
  };

  // Rollback was only reachable from a screen that no longer exists, so once
  // that screen was removed there was no way left to undo a bad deploy.
  const canRollback = () => {
    return (currentUser.role === 'devops' || isAdmin) &&
      (deployment.status === 'deployed' || isEmergencyLive);
  };

  // An emergency-deployed request is already live — the backend refuses
  // reject there (rollback is the right action), so don't offer it.
  const canReject = () => {
    if (isEmergencyLive) return false;
    return tlCanAct || pmCanAct ||
      (currentUser.role === 'devops' && deployment.status === 'pm_approved') ||
      (isAdmin && isOpenStatus);
  };

  // PM-stage approval (by a PM, or an admin acting for one) needs a release note.
  const needsReleaseNote = actionType === 'approve' && (currentUser.role === 'pm' || isAdmin) &&
    deployment.status === 'tl_approved' && isReleaseNoteApplicable;

  const canEdit = () => {
    // Allow edit if user is the creator and status is pending or tl_approved
    const isCreator = deployment.requestedBy?.id === currentUser.id || 
                      deployment.requestedBy?.email === currentUser.email;
    const editableStatus = deployment.status === 'pending' || deployment.status === 'tl_approved';
    return isCreator && editableStatus && ['developer', 'tl', 'pm'].includes(currentUser.role);
  };

  const handleActionSubmit = async () => {
    const deploymentId = (deployment as any)._id || deployment.id;
    if (!actionType || !deploymentId) {
      console.error('Cannot submit action: missing deploymentId or actionType', { deploymentId, actionType });
      return;
    }
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setSubmittingAction(true);

    try {
      await onAction?.(actionType, deploymentId, actionDescription, actionReleaseNote);
      setShowActionModal(false);
      setActionType(null);
      setActionDescription('');
      setActionReleaseNote('');
    } catch (error) {
      // App's handler already toasted the server's message; keep the modal
      // (and whatever was typed into it) open so the user can fix and retry.
      console.error(`Error ${actionType}ing deployment:`, error);
    } finally {
      actionInFlightRef.current = false;
      setSubmittingAction(false);
    }
  };

  const handleEditSubmit = async () => {
    const deploymentId = (deployment as any)._id || deployment.id;
    if (!deploymentId || !editFormData) {
      console.error('Cannot submit edit: missing deploymentId or editFormData');
      return;
    }
    if (savingEdit) return;
    setSavingEdit(true);
    
    try {
      
      // Convert datetime-local to ISO string if needed
      const dataToSubmit = {
        title: editFormData.title,
        description: editFormData.description,
        priority: editFormData.priority,
        deploymentType: editFormData.deploymentType,
        scheduledDate: editFormData.scheduledDate ? new Date(editFormData.scheduledDate).toISOString() : editFormData.scheduledDate,
        dbScript: editFormData.dbScript,
        dbScriptFileName: editFormData.dbScriptFileName,
        // Send modules with their optional branch; backend resolves names from the IDs
        modules: (editFormData.modules || []).map((moduleId: string) => ({
          _id: moduleId,
          branch: (editModuleBranches[moduleId] || '').trim()
        })),
        institutes: editFormData.institutes || [],
        // Send testers as array of IDs (backend will handle lookup)
        testers: (editFormData.testers || []).map((testerId: string) => {
          const tester = availableTesters.find(t => (t._id || t.id) === testerId);
          return {
            id: tester?._id || tester?.id || testerId,
            name: tester?.name || tester?.username || tester?.email || testerId,
            email: tester?.email || '',
            username: tester?.username || tester?.name || ''
          };
        }),
        // Send TLs as simple array of IDs (backend will handle lookup)
        tls: editFormData.tls || [],
        // Send PMs as simple array of IDs (backend will handle lookup)
        pms: editFormData.pms || [],
        // Send DevOps assignees as simple array of IDs (backend will handle lookup)
        devopsAssignees: editFormData.devopsAssignees || []
      };
      
      const response = await fetch(`/api/deployments/${deploymentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSubmit)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to update deployment');
      }

      const updatedDeployment = await response.json();
      
      // Update local state without reloading
      if (onUpdate) {
        onUpdate(updatedDeployment);
      }
      
      toast('Deployment updated successfully!');
      setShowEditModal(false);
      setEditFormData(null);
    } catch (error) {
      console.error('Error updating deployment:', error);
      toast(`Failed to update deployment. ${error instanceof Error ? error.message : 'Please try again.'}`, 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleInstitute = async (instituteId: string, instituteName: string) => {
    const deploymentId = (deployment as any)._id || deployment.id;
    if (!deploymentId) {
      console.error('Cannot toggle institute: missing deploymentId');
      return;
    }

    try {
      setTogglingInstitute(true);
      
      const response = await fetch(`/api/deployments/${deploymentId}/toggle-institute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          instituteId,
          instituteName
        })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to update institute status');
      }

      const updatedDeployment = await response.json();
      
      // Update local state
      if (updatedDeployment.deployment && updatedDeployment.deployment.deployedInstitutes) {
        setDeployedInstitutes(updatedDeployment.deployment.deployedInstitutes.map((inst: any) => inst.instituteId));
      }
      
    } catch (error) {
      console.error('Error toggling institute:', error);
      toast(error instanceof Error ? error.message : 'Failed to update institute status. Please try again.', 'error');
    } finally {
      setTogglingInstitute(false);
    }
  };

  const handleNotifyProgress = async () => {
    const deploymentId = (deployment as any)._id || deployment.id;
    if (!deploymentId) return;

    try {
      setNotifyingProgress(true);
      const response = await fetch(`/api/deployments/${deploymentId}/notify-institute-progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to send notification');
      }

      toast('Progress update sent to the requester and approvers.');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Failed to send notification. Please try again.');
    } finally {
      setNotifyingProgress(false);
    }
  };

  const isInstituteDeployed = (instituteId: string) => {
    return deployedInstitutes.includes(instituteId);
  };

  const allInstitutesDeployed = () => {
    if (!deployment.institutes || deployment.institutes.length === 0) {
      return true; // No institutes to deploy
    }
    return deployment.institutes.every((inst: any) => {
      const instId = inst._id || inst.id || inst;
      return deployedInstitutes.includes(instId);
    });
  };

  const priorityAccent = getPriorityAccent(deployment.priority);

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 ${priorityAccent.border} overflow-hidden transition-all hover:shadow-md`}>
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${priorityAccent.chip} ${priorityAccent.text} font-bold text-sm`}>
              #{deployment.deploymentNumber || (deployment as any)._id || deployment.id || '0'}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{deployment.title || 'Untitled'}</h2>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Approval Notes Button (shows TL/PM/Deploy descriptions) */}
            {((deployment.tlApprovals && deployment.tlApprovals.some((a: any) => a.description)) ||
              (deployment.pmApprovals && deployment.pmApprovals.some((a: any) => a.description)) ||
              (deployment.deployment && deployment.deployment.description)) && (
              <button
                onClick={() => setShowDescriptionModal(true)}
                className="px-3 py-1.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 transition-colors flex items-center gap-1.5"
                title="View approval notes"
              >
                <FileText className="w-3.5 h-3.5" />
                View Description
              </button>
            )}
            {/* Action buttons in header */}
            {showActions && (
              <div className="flex gap-2">
                {canEdit() && (
                  <button
                    onClick={async () => {
                      // Fetch available users, modules, and institutes
                      try {
                        const [testersRes, usersRes, modulesRes, institutesRes] = await Promise.all([
                          fetch('/api/testers'),
                          fetch('/api/users'),
                          fetch('/api/modules'),
                          fetch('/api/institutes')
                        ]);

                        const testersData = await testersRes.json();
                        const usersData = await usersRes.json();
                        const modulesData = await modulesRes.json();
                        const institutesData = await institutesRes.json();

                        const tlsList = usersData.filter((u: any) => {
                          const roles = u.realmRoles || u.roles || [];
                          return roles.some((r: string) => r.toLowerCase() === 'tl') || u.role === 'tl';
                        });

                        const pmsList = usersData.filter((u: any) => {
                          const roles = u.realmRoles || u.roles || [];
                          return roles.some((r: string) => r.toLowerCase() === 'pm') || u.role === 'pm';
                        });

                        const devopsList = usersData.filter((u: any) => {
                          const roles = u.realmRoles || u.roles || [];
                          return roles.some((r: string) => r.toLowerCase() === 'devops') || u.role === 'devops';
                        });

                        setAvailableTesters(testersData);
                        setAvailableTLs(tlsList);
                        setAvailablePMs(pmsList);
                        setAvailableDevops(devopsList);
                        setAvailableModules(modulesData);
                        setAvailableInstitutes(institutesData);

                        // Extract IDs from testers, TLs, PMs - handle both id and _id.
                        // Prefer `.id` (the real Keycloak user id we store) over `._id` —
                        // Mongoose auto-assigns a meaningless `_id` to each array element
                        // here since testers/devopsAssignees are stored as direct array
                        // items (unlike tlApprovals/pmApprovals, where the user reference
                        // is nested one level deeper and never gets an auto `_id`).
                        // Picking `._id` sent that bogus id back on save, which then
                        // failed to resolve against Keycloak and got persisted as both
                        // the id and the display name.
                        const testerIds = Array.isArray(deployment.testers)
                          ? deployment.testers.map((t: any) => {
                              if (typeof t === 'object' && t) {
                                return String(t.id || t._id);
                              }
                              return String(t);
                            })
                          : [];

                        // Extract TL IDs and verify they exist in available list
                        const tlIds = Array.isArray(deployment.tlApprovals)
                          ? deployment.tlApprovals
                              .map((tl: any) => {
                                const tlUser = tl.tl;
                                if (tlUser && typeof tlUser === 'object') {
                                  return String(tlUser._id || tlUser.id);
                                }
                                return String(tlUser);
                              })
                              .filter((id: string) => tlsList.some((tl: any) => String(tl._id || tl.id) === id))
                          : [];

                        // Extract PM IDs and verify they exist in available list
                        const pmIds = Array.isArray(deployment.pmApprovals)
                          ? deployment.pmApprovals
                              .map((pm: any) => {
                                const pmUser = pm.pm;
                                if (pmUser && typeof pmUser === 'object') {
                                  return String(pmUser._id || pmUser.id);
                                }
                                return String(pmUser);
                              })
                              .filter((id: string) => pmsList.some((pm: any) => String(pm._id || pm.id) === id))
                          : [];

                        // Same `.id` vs `._id` issue as testers above.
                        const devopsIds = Array.isArray((deployment as any).devopsAssignees)
                          ? (deployment as any).devopsAssignees.map((d: any) => String(d.id || d._id))
                          : [];

                        const moduleIds = Array.isArray(deployment.modules)
                          ? deployment.modules.map((m: any) => String(m._id || m.id))
                          : [];

                        const branchesByModuleId: Record<string, string> = {};
                        if (Array.isArray(deployment.modules)) {
                          deployment.modules.forEach((m: any) => {
                            if (m && (m._id || m.id) && m.branch) {
                              branchesByModuleId[String(m._id || m.id)] = m.branch;
                            }
                          });
                        }
                        setEditModuleBranches(branchesByModuleId);

                        const instituteIds = Array.isArray(deployment.institutes)
                          ? deployment.institutes.map((i: any) => String(i._id || i.id))
                          : [];

                        setEditFormData({
                          title: deployment.title,
                          description: deployment.description,
                          priority: deployment.priority,
                          deploymentType: (deployment as any).deploymentType,
                          scheduledDate: deployment.scheduledDate,
                          dbScript: (deployment as any).dbScript || '',
                          dbScriptFileName: (deployment as any).dbScriptFileName || '',
                          modules: moduleIds,
                          institutes: instituteIds,
                          testers: testerIds,
                          tls: tlIds,
                          pms: pmIds,
                          devopsAssignees: devopsIds
                        });
                        setShowEditModal(true);
                      } catch (err) {
                        console.error('Error fetching users:', err);
                        toast('Failed to load users. Please try again.');
                      }
                    }}
                    className="px-4 py-2 bg-yellow-600 text-white text-xs font-semibold rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-1 transition-colors shadow-sm flex items-center gap-1.5"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    Edit
                  </button>
                )}
                {canApprove() && (
                  <button
                    onClick={() => handleActionClick('approve')}
                    className="px-4 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!((deployment as any)._id || deployment.id)}
                  >
                    Approve
                  </button>
                )}
                {canDeploy() && (
                  <button
                    onClick={() => handleActionClick('deploy')}
                    className={`px-4 py-2 text-white text-xs font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                      allInstitutesDeployed() 
                        ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                        : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                    }`}
                    disabled={!((deployment as any)._id || deployment.id) || !allInstitutesDeployed()}
                    title={!allInstitutesDeployed() ? 'Please check all institutes before deploying' : ''}
                  >
                    {allInstitutesDeployed() ? '✓ Deployed' : 'Deploy'}
                  </button>
                )}
                {canReject() && (
                  <button
                    onClick={() => handleActionClick('reject')}
                    className="px-4 py-2 bg-red-600 text-white text-xs font-semibold rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!((deployment as any)._id || deployment.id)}
                  >
                    Reject
                  </button>
                )}
              </div>
            )}
            {/* Deliberately outside the showActions gate — this needs to be
                reachable from read-only overview pages too (e.g. "All
                Requests"), since that's exactly where DevOps would be
                browsing when they notice a request that needs to go live
                despite an absent TL/PM, not just from their normal queue. */}
            {canEmergencyDeploy() && (
              <button
                onClick={() => handleActionClick('emergency-deploy')}
                className="px-4 py-2 bg-red-700 text-white text-xs font-semibold rounded-md hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-1 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!((deployment as any)._id || deployment.id)}
                title="Deploy live now, regardless of approval status"
              >
                🚨 Emergency Deploy
              </button>
            )}
            {canRollback() && (
              <button
                onClick={() => handleActionClick('rollback')}
                className="px-4 py-2 bg-orange-600 text-white text-xs font-semibold rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!((deployment as any)._id || deployment.id)}
                title="Roll this deployment back and notify the requester"
              >
                ⏪ Rollback
              </button>
            )}
            {isEmergencyLive ? (
              <span
                className="px-3 py-1.5 rounded-full text-xs font-semibold border bg-red-100 text-red-800 border-red-300"
                title={deployment.deployment?.emergencyReason ? `Emergency reason: ${deployment.deployment.emergencyReason}` : undefined}
              >
                🚨 Live — Approval Pending
              </span>
            ) : (
              <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${getStatusBadgeColor(deployment.status)}`}>
                {getStatusText(deployment.status)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Four info boxes */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-6 bg-gray-50">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Requested By</div>
          <div className="flex items-center gap-2">
            <Avatar name={deployment.requestedBy?.name} userId={(deployment.requestedBy as any)?.id} size="xs" />
            <span className="text-base font-semibold text-gray-900">{deployment.requestedBy?.username || deployment.requestedBy?.name || 'Unknown'}</span>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Requested Time</div>
          <div className="text-base font-semibold text-gray-900">
            {deployment.requestedAt ? new Date(deployment.requestedAt).toLocaleString('en-GB', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit', 
              hour12: true 
            }) : '—'}
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Deployment Time</div>
          <div className="text-base font-semibold text-gray-900">
            {deployment.scheduledDate ? new Date(deployment.scheduledDate).toLocaleString('en-GB', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit', 
              hour12: true 
            }) : '—'}
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Module</div>
          <div className="flex flex-wrap gap-1.5">
            {getModulesWithBranch(deployment.modules, deployment.module).length === 0 ? (
              <span className="text-base font-semibold text-gray-900">—</span>
            ) : (
              getModulesWithBranch(deployment.modules, deployment.module).map(({ name, branch }) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-100 text-sm font-semibold text-gray-800"
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: getModuleColor(name) }}
                  />
                  {name}
                  {branch && (
                    <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-mono normal-case">
                      {branch}
                    </span>
                  )}
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Scheduled Deployment and Approval Progress - Combined */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Scheduled:</span>
            <span className="text-sm font-semibold text-gray-900">
              {deployment.scheduledDate ? new Date(deployment.scheduledDate).toLocaleString('en-GB', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric', 
                hour: '2-digit', 
                minute: '2-digit', 
                hour12: true 
              }) : '—'}
            </span>
          </div>
        </div>
        <ApprovalTimeline deployment={deployment} />
      </div>

      {/* Priority & Tags */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          {deployment.priority && (
            <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${priorityAccent.chip} ${priorityAccent.text} border-transparent`}>
              {deployment.priority.toUpperCase()} PRIORITY
            </span>
          )}
          {deployment.deploymentType && (
            <span className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200">
              {deployment.deploymentType === 'feature' ? '✨ Feature' :
               deployment.deploymentType === 'bug' ? '🐛 Bugfix' :
               deployment.deploymentType === 'payload' ? '📦 Payload' :
               deployment.deploymentType === 'version' ? '🏷️ Version' :
               deployment.deploymentType === 'new-deployment' ? '🆕 New Deployment' :
               deployment.deploymentType === 'db-script' ? '🗄️ DB Script' :
               deployment.deploymentType}
            </span>
          )}
        </div>
      </div>

      {/* Release Note - Always Visible (except DB Script / New Deployment) */}
      {isReleaseNoteApplicable && (deployment as any).releaseNote && (
        <div className="px-6 py-4 border-b border-gray-200 bg-amber-50">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">📋 Release Note</div>
              <p className="text-sm text-amber-900 whitespace-pre-wrap break-words leading-relaxed">
                {(deployment as any).releaseNote}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Show More/Less toggle button */}
      <div className="border-b border-gray-200">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-6 py-4 flex items-center justify-center gap-2 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors"
        >
          {isExpanded ? (
            <>
              <span className="text-base">▲</span>
              Show Less
            </>
          ) : (
            <>
              <span className="text-base">▼</span>
              Show More
            </>
          )}
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 py-6 bg-gray-50 space-y-6">
          {/* Description - Full Text */}
          {deployment.description && (
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-500 mb-2">Description</div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
                    {deployment.description}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Additional Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Testers */}
            <div className="flex items-start gap-3 bg-white p-4 rounded-lg border border-gray-200">
              <User className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-500">Testers</div>
                <div className="text-base font-semibold text-gray-900 mt-1">
                  {deployment.testers && Array.isArray(deployment.testers) && deployment.testers.length > 0
                    ? deployment.testers.map((t: any) => typeof t === 'object' && t ? (t.username || t.name || 'Unknown') : t).join(', ')
                    : '—'}
                </div>
              </div>
            </div>

            {/* Requested by */}
            <div className="flex items-start gap-3 bg-white p-4 rounded-lg border border-gray-200">
              <Avatar name={deployment.requestedBy?.name} userId={(deployment.requestedBy as any)?.id} size="sm" className="mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-500">Requested by</div>
                <div className="text-base font-semibold text-gray-900 mt-1">
                  {deployment.requestedBy?.username || deployment.requestedBy?.name || 'Unknown'}
                </div>
              </div>
            </div>

            {/* Institutes with Checkboxes */}
            <div className="flex items-start gap-3 bg-white p-4 rounded-lg border border-gray-200 md:col-span-2">
              <Package className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-500 mb-2">Institutes</div>
                {deployment.institutes && Array.isArray(deployment.institutes) && deployment.institutes.length > 0 ? (
                  (currentUser.role === 'devops' || isAdmin) && deployment.status === 'pm_approved' ? (
                    // DevOps users see checkboxes for PM approved deployments
                    <div className="space-y-2">
                    {deployment.institutes.map((inst: any) => {
                      const instId = inst._id || inst.id || inst;
                      const instName = inst.name || inst;
                      const isDeployed = isInstituteDeployed(instId);
                      
                      return (
                        <label 
                          key={instId} 
                          className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${
                            isDeployed ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isDeployed}
                            onChange={() => handleToggleInstitute(instId, instName)}
                            disabled={togglingInstitute || deployment.status === 'deployed'}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className={`text-sm font-medium ${isDeployed ? 'text-green-700' : 'text-gray-700'}`}>
                            {instName}
                          </span>
                          {isDeployed && (
                            <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />
                          )}
                        </label>
                      );
                    })}
                    <button
                      type="button"
                      onClick={handleNotifyProgress}
                      disabled={notifyingProgress || deployedInstitutes.length === 0}
                      title={deployedInstitutes.length === 0 ? 'Check off at least one institute first' : 'Email the requester and approvers with current progress'}
                      className="mt-1 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-50 transition-colors"
                    >
                      🔔 {notifyingProgress ? 'Sending…' : `Notify progress (${deployedInstitutes.length}/${deployment.institutes.length})`}
                    </button>
                  </div>
                  ) : (
                    // Other users see badge display only
                    <div className="flex flex-wrap gap-2">
                      {deployment.institutes.map((inst: any) => {
                        const instId = inst._id || inst.id || inst;
                        const instName = inst.name || inst;
                        const isDeployed = isInstituteDeployed(instId);
                        
                        return (
                          <span
                            key={instId}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border-2 ${
                              isDeployed
                                ? 'bg-green-50 text-green-700 border-green-400'
                                : 'bg-gray-50 text-gray-700 border-gray-300'
                            }`}
                          >
                            {isDeployed && <CheckCircle className="w-4 h-4" />}
                            {instName}
                          </span>
                        );
                      })}
                    </div>
                  )
                ) : (
                  <div className="text-base font-semibold text-gray-900">
                    {getInstituteNames(deployment.institutes, deployment.institute)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* DB Script */}
          {(deployment.dbScript || deployment.dbScriptFileName) && (
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-500">DB Script</div>
                  <button
                    onClick={() => setShowDbScriptModal(true)}
                    className="text-base font-semibold text-blue-600 hover:text-blue-700 hover:underline mt-1 inline-block break-all"
                  >
                    {deployment.dbScriptFileName || 'View Script'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Approval History */}
          <div className="bg-white p-5 rounded-lg border border-gray-200">
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Approval History
            </h3>
            <div className="space-y-3">
              {/* TL Approvals - Show both approved and pending */}
              {Array.isArray(deployment.tlApprovals) && deployment.tlApprovals.length > 0 && (
                <>
                  {deployment.tlApprovals.map((a, idx) => {
                    if (a.approved && a.tl && a.tl.name) {
                      return (
                        <div className="flex items-start gap-3 pl-2 border-l-2 border-emerald-200 ml-2" key={`tl-approved-${idx}`}>
                          <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  TL Approved: {a.tl.username || a.tl.name}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {a.approvedAt ? new Date(a.approvedAt).toLocaleString('en-GB', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric', 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: true 
                                  }) : ''}
                                </div>
                              </div>
                              {(a as any).description && (
                                <button
                                  onClick={() => toast(`TL Note:\n\n${(a as any).description}`)}
                                  className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                  title="View approval note"
                                >
                                  📝 Note
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    } else if (!a.approved && a.tl && a.tl.name) {
                      // Show pending TL with name
                      return (
                        <div className="flex items-start gap-3 pl-2 border-l-2 border-amber-200 ml-2" key={`tl-pending-${idx}`}>
                          <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              TL Pending: {a.tl.username || a.tl.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Waiting for approval</div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </>
              )}

              {/* PM Approvals - Show both approved and pending */}
              {Array.isArray(deployment.pmApprovals) && deployment.pmApprovals.length > 0 && (
                <>
                  {deployment.pmApprovals.map((a, idx) => {
                    if (a.approved && a.pm && a.pm.name) {
                      return (
                        <div className="flex items-start gap-3 pl-2 border-l-2 border-emerald-200 ml-2" key={`pm-approved-${idx}`}>
                          <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  PM Approved: {a.pm.username || a.pm.name}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {a.approvedAt ? new Date(a.approvedAt).toLocaleString('en-GB', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric', 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: true 
                                  }) : ''}
                                </div>
                              </div>
                              {(a as any).description && (
                                <button
                                  onClick={() => toast(`PM Note:\n\n${(a as any).description}`)}
                                  className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                  title="View approval note"
                                >
                                  📝 Note
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    } else if (!a.approved && a.pm && a.pm.name) {
                      // Show pending PM with name
                      return (
                        <div className="flex items-start gap-3 pl-2 border-l-2 border-amber-200 ml-2" key={`pm-pending-${idx}`}>
                          <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              PM Pending: {a.pm.username || a.pm.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Waiting for approval</div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </>
              )}

              {/* Old PM Approval format (for backward compatibility) */}
              {deployment.pmApproval && deployment.pmApproval.approvedBy && (
                <div className="flex items-start gap-3 pl-2 border-l-2 border-emerald-200 ml-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      PM Approved: {deployment.pmApproval.approvedBy.username || deployment.pmApproval.approvedBy.name}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {deployment.pmApproval.approvedAt ? new Date(deployment.pmApproval.approvedAt).toLocaleString('en-GB', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric', 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: true 
                      }) : ''}
                    </div>
                  </div>
                </div>
              )}

              {/* Deployment Status - Completed (or already live via emergency deploy) */}
              {(deployment.status === 'deployed' || isEmergencyLive) && deployment.deployment && deployment.deployment.deployedBy && (
                <div className={`flex items-start gap-3 pl-2 border-l-2 ml-2 ${isEmergencyLive ? 'border-red-300' : 'border-emerald-200'}`}>
                  <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isEmergencyLive ? 'text-red-600' : 'text-emerald-600'}`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {isEmergencyLive ? '🚨 Emergency deployed by: ' : 'Deployed by: '}
                          {deployment.deployment.deployedBy.username || deployment.deployment.deployedBy.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {deployment.deployment.deployedAt ? new Date(deployment.deployment.deployedAt).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          }) : ''}
                        </div>
                        {deployment.deployment.isEmergency && deployment.deployment.emergencyReason && (
                          <div className="text-xs text-red-700 mt-1">
                            Reason: {deployment.deployment.emergencyReason}
                          </div>
                        )}
                        {isEmergencyLive && (
                          <div className="text-xs text-amber-700 mt-1 font-medium">
                            Still awaiting TL/PM approval to formally close this request — this date won't change once it does.
                          </div>
                        )}
                      </div>
                      {deployment.deployment.description && (
                        <button
                          onClick={() => deployment.deployment && toast(`Deployment Note:\n\n${deployment.deployment.description}`)}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                          title="View deployment note"
                        >
                          📝 Note
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Deployment Status - Pending (show for non-deployed statuses that also aren't already emergency-live) */}
              {deployment.status !== 'deployed' && !isEmergencyLive && (
                <div className="flex items-start gap-3 pl-2 border-l-2 border-amber-200 ml-2">
                  <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">Deployment: Pending</div>
                    <div className="text-xs text-gray-500 mt-1">Waiting for deployment</div>
                  </div>
                </div>
              )}

              {/* No approvals yet */}
              {(!deployment.tlApprovals || deployment.tlApprovals.length === 0) && (!deployment.pmApprovals || deployment.pmApprovals.length === 0) && !deployment.pmApproval && (
                <div className="text-sm text-gray-500 italic">No approvals yet</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DB Script Modal */}
      {showDbScriptModal && (deployment.dbScript || deployment.dbScriptFileName) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowDbScriptModal(false); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Package className="w-6 h-6 text-blue-600" />
                DB Script: {deployment.dbScriptFileName || 'Script Content'}
              </h3>
              <button
                onClick={() => setShowDbScriptModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6">
              <DbScriptViewer script={deployment.dbScript || ''} />
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                <span className="font-medium">File:</span> {deployment.dbScriptFileName || 'N/A'} 
                {deployment.dbScript && (
                  <span className="ml-4">
                    <span className="font-medium">Size:</span> {deployment.dbScript.length} characters
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (deployment.dbScript) {
                      navigator.clipboard.writeText(deployment.dbScript);
                      toast('Script copied to clipboard!');
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  📋 Copy
                </button>
                <button
                  onClick={() => {
                    if (deployment.dbScript) {
                      const blob = new Blob([deployment.dbScript], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = deployment.dbScriptFileName || 'db-script.sql';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  💾 Download
                </button>
                <button
                  onClick={() => setShowDbScriptModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal for optional description */}
      {showActionModal && actionType && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => { if (!submittingAction) setShowActionModal(false); }}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">
                {actionType === 'emergency-deploy' ? '🚨 Emergency Deploy' : actionType === 'rollback' ? '⏪ Roll Back Deployment' : <span className="capitalize">{actionType} Deployment</span>}
              </h3>
              {actionType === 'emergency-deploy' && (
                <p className="text-sm text-gray-600 mb-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  This marks the request as already live in production right now, regardless of approval status. TL/PM approval keeps running in the background and just finalizes the ticket once it lands — the deploy time recorded here won't be overwritten.
                </p>
              )}
              {actionType === 'rollback' && (
                <p className="text-sm text-gray-600 mb-4 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                  This marks the deployment as rolled back, releases its booked slot and notifies the requester. The previous version must be restored on the servers separately.
                </p>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {(actionType === 'emergency-deploy' || actionType === 'rollback') ? <>Reason <span className="text-red-500">*</span></> : 'Description (Optional)'}
                </label>
                <textarea
                  value={actionDescription}
                  onChange={(e) => setActionDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={3}
                  placeholder={actionType === 'emergency-deploy' ? 'Why is this being deployed ahead of approval? (required)' : actionType === 'rollback' ? 'Why is this being rolled back? (required)' : `Add a note about this ${actionType}...`}
                />
              </div>

              {/* Release Note - Required for PM approvals when deployment is tl_approved (except DB Script / New Deployment) */}
              {needsReleaseNote && (() => {
                // Normalize: lowercase, strip punctuation/extra spaces
                const normalize = (s: string) =>
                  s.toLowerCase().replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();

                // Word-level Jaccard similarity: overlap / union
                const similarity = (a: string, b: string): number => {
                  const wordsA = new Set(normalize(a).split(' ').filter(Boolean));
                  const wordsB = new Set(normalize(b).split(' ').filter(Boolean));
                  if (wordsA.size === 0 || wordsB.size === 0) return 0;
                  const intersection = [...wordsA].filter(w => wordsB.has(w)).length;
                  const union = new Set([...wordsA, ...wordsB]).size;
                  return intersection / union;
                };

                const releaseVal = actionReleaseNote.trim();
                const deployDesc = (deployment.description || '').trim();
                const modalDesc = actionDescription.trim();

                const normRelease = normalize(releaseVal);
                const normDeploy  = normalize(deployDesc);
                const normModal   = normalize(modalDesc);

                // Block if: exact match, one contains the other, or >70% word overlap
                const checkAgainst = (ref: string, normRef: string): boolean => {
                  if (!releaseVal || !ref) return false;
                  if (normRelease === normRef) return true;
                  if (normRelease.includes(normRef) || normRef.includes(normRelease)) return true;
                  if (similarity(releaseVal, ref) >= 0.7) return true;
                  return false;
                };

                const isSame = checkAgainst(deployDesc, normDeploy) || checkAgainst(modalDesc, normModal);
                return (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Release Note <span className="text-red-500">*</span> — link or paragraph
                      </label>
                      {/* Show deployment description as hint */}
                      {deployment.description && (
                        <p className="text-xs text-gray-500 mb-2 bg-gray-50 px-2 py-1 rounded border border-gray-200">
                          <span className="font-semibold">Deployment description:</span> {deployment.description}
                        </p>
                      )}
                      <textarea
                        value={actionReleaseNote}
                        onChange={(e) => setActionReleaseNote(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                          isSame ? 'border-red-400 bg-red-50' : 'border-gray-300'
                        }`}
                        rows={4}
                        placeholder="Add a release note (URL or paragraph). Must be different from the description."
                      />
                    </div>

                    {/* Validation warning */}
                    {isSame && (
                      <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-4 py-3">
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700 font-medium">
                          Release Note cannot be the same as the Description. Please enter unique release note content.
                        </p>
                      </div>
                    )}

                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={() => setShowActionModal(false)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleActionSubmit}
                        disabled={!releaseVal || isSame || submittingAction}
                        className={`px-4 py-2 text-white rounded-lg transition-colors bg-green-600 hover:bg-green-700 ${
                          (!releaseVal || isSame || submittingAction) ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {submittingAction ? 'Working…' : 'Confirm Approval'}
                      </button>
                    </div>
                  </>
                );
              })()}

              {/* For non-PM actions (TL approve, reject, deploy) */}
              {!needsReleaseNote && (
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowActionModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleActionSubmit}
                    disabled={submittingAction || ((actionType === 'emergency-deploy' || actionType === 'rollback') && !actionDescription.trim())}
                    className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                      actionType === 'deploy' ? 'bg-blue-600 hover:bg-blue-700' :
                      actionType === 'emergency-deploy' ? 'bg-red-600 hover:bg-red-700' :
                      actionType === 'rollback' ? 'bg-orange-600 hover:bg-orange-700' :
                      'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {submittingAction ? 'Working…' :
                     actionType === 'approve' ? 'Confirm Approval' :
                     actionType === 'deploy' ? 'Confirm Deployment' :
                     actionType === 'emergency-deploy' ? 'Confirm Emergency Deploy' :
                     actionType === 'rollback' ? 'Confirm Rollback' :
                     'Confirm Rejection'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Description Modal - Shows Approval/Rejection Notes */}
      {showDescriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowDescriptionModal(false); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-blue-600" />
                Approval & Deployment Notes
              </h3>
              <button
                onClick={() => setShowDescriptionModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6 space-y-4">
              {/* TL Approval Notes */}
              {deployment.tlApprovals && deployment.tlApprovals.filter((a: any) => a.description).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">TL Approval Notes:</h4>
                  {deployment.tlApprovals.filter((a: any) => a.description).map((approval: any, idx: number) => (
                    <div key={idx} className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-900">
                          {approval.tl?.username || approval.tl?.name || 'TL'}
                        </span>
                        <span className="text-xs text-blue-600">
                          {approval.approvedAt ? new Date(approval.approvedAt).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          }) : ''}
                        </span>
                      </div>
                      <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {approval.description}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* PM Approval Notes */}
              {deployment.pmApprovals && deployment.pmApprovals.filter((a: any) => a.description).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">PM Approval Notes:</h4>
                  {deployment.pmApprovals.filter((a: any) => a.description).map((approval: any, idx: number) => (
                    <div key={idx} className="bg-green-50 p-4 rounded-lg border border-green-200 mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-green-900">
                          {approval.pm?.username || approval.pm?.name || 'PM'}
                        </span>
                        <span className="text-xs text-green-600">
                          {approval.approvedAt ? new Date(approval.approvedAt).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          }) : ''}
                        </span>
                      </div>
                      <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {approval.description}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Deployment Note */}
              {deployment.deployment && deployment.deployment.description && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Deployment Note:</h4>
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-purple-900">
                        {deployment.deployment.deployedBy?.username || deployment.deployment.deployedBy?.name || 'DevOps'}
                      </span>
                      <span className="text-xs text-purple-600">
                        {deployment.deployment.deployedAt ? new Date(deployment.deployment.deployedAt).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        }) : ''}
                      </span>
                    </div>
                    <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap break-words">
                      {deployment.deployment.description}
                    </p>
                  </div>
                </div>
              )}

              {/* No Notes */}
              {(!deployment.tlApprovals || deployment.tlApprovals.filter((a: any) => a.description).length === 0) &&
               (!deployment.pmApprovals || deployment.pmApprovals.filter((a: any) => a.description).length === 0) &&
               (!deployment.deployment || !deployment.deployment.description) && (
                <div className="text-center text-gray-500 py-8">
                  <p>No approval or deployment notes available.</p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Request:</span> {deployment.title}
              </div>
              <button
                onClick={() => setShowDescriptionModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editFormData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowEditModal(false); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Edit className="w-6 h-6 text-yellow-600" />
                Edit Deployment Request
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={editFormData.title || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={editFormData.description || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select
                    value={editFormData.priority || 'medium'}
                    onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="high">🔴 High</option>
                    <option value="medium">🟡 Medium</option>
                    <option value="low">🟢 Low</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Deployment Type</label>
                  <select
                    value={editFormData.deploymentType || 'feature'}
                    onChange={(e) => setEditFormData({ ...editFormData, deploymentType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="bug">🐛 Bug Fix</option>
                    <option value="feature">✨ Feature</option>
                    <option value="payload">📦 Payload</option>
                    <option value="version">🔢 Version Update</option>
                    <option value="new-deployment">🚀 New Deployment</option>
                    <option value="db-script">🗄️ DB Script</option>
                  </select>
                </div>

                {/* Modules Multi-Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Modules</label>
                  <select
                    multiple
                    value={editFormData.modules || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setEditFormData({ ...editFormData, modules: selected });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    size={5}
                  >
                    {availableModules.map((mod: any) => (
                      <option key={mod._id || mod.id} value={mod._id || mod.id}>
                        {mod.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple modules</p>
                  {(editFormData.modules || []).length > 0 && (
                    <div className="mt-3 space-y-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-xs font-medium text-gray-500">
                        Branch name <span className="text-gray-400">(optional, per module)</span>
                      </p>
                      {(editFormData.modules || []).map((moduleId: string) => {
                        const mod = availableModules.find((m: any) => (m._id || m.id) === moduleId);
                        return (
                          <div key={moduleId} className="flex items-center gap-2">
                            <span className="w-40 flex-shrink-0 truncate text-sm text-gray-700 font-medium">
                              {mod?.name || moduleId}
                            </span>
                            <input
                              type="text"
                              value={editModuleBranches[moduleId] || ''}
                              onChange={(e) =>
                                setEditModuleBranches(prev => ({ ...prev, [moduleId]: e.target.value }))
                              }
                              placeholder="e.g. feature/login-fix"
                              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Institutes Multi-Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Institutes</label>
                  <select
                    multiple
                    value={editFormData.institutes || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setEditFormData({ ...editFormData, institutes: selected });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    size={5}
                  >
                    {availableInstitutes.map((inst: any) => (
                      <option key={inst._id || inst.id} value={inst._id || inst.id}>
                        {inst.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple institutes</p>
                </div>

                {/* DB Script is optional regardless of deployment type — matches create form */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">DB Script (optional)</label>
                  <textarea
                    value={editFormData.dbScript || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, dbScript: e.target.value })}
                    rows={5}
                    placeholder="Paste SQL / migration script here (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">DB Script Filename (optional)</label>
                  <input
                    type="text"
                    value={editFormData.dbScriptFileName || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, dbScriptFileName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Scheduled Date & Time</label>
                  <input
                    type="datetime-local"
                    value={editFormData.scheduledDate ? (() => {
                      // Convert to local timezone for display
                      const date = new Date(editFormData.scheduledDate);
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      return `${year}-${month}-${day}T${hours}:${minutes}`;
                    })() : ''}
                    onChange={(e) => {
                      // Convert local time to ISO string
                      const localDateTime = e.target.value;
                      if (localDateTime) {
                        const date = new Date(localDateTime);
                        setEditFormData({ ...editFormData, scheduledDate: date.toISOString() });
                      } else {
                        setEditFormData({ ...editFormData, scheduledDate: '' });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Testers Multi-Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Testers</label>
                  <select
                    multiple
                    value={editFormData.testers || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setEditFormData({ ...editFormData, testers: selected });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    size={5}
                  >
                    {availableTesters.map(tester => (
                      <option key={tester._id || tester.id} value={tester._id || tester.id}>
                        {tester.username || tester.name || tester.email}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple testers</p>
                </div>

                {/* TLs Multi-Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Team Leads (TLs)</label>
                  <select
                    multiple
                    value={editFormData.tls || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setEditFormData({ ...editFormData, tls: selected });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    size={5}
                  >
                    {availableTLs.map(tl => (
                      <option key={tl._id || tl.id} value={tl._id || tl.id}>
                        {tl.username || tl.name} - {tl.email}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple TLs</p>
                </div>

                {/* PMs Multi-Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Program Managers (PMs)</label>
                  <select
                    multiple
                    value={editFormData.pms || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setEditFormData({ ...editFormData, pms: selected });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    size={5}
                  >
                    {availablePMs.map(pm => (
                      <option key={pm._id || pm.id} value={pm._id || pm.id}>
                        {pm.username || pm.name} - {pm.email}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple PMs</p>
                </div>

                {/* DevOps Assignees Multi-Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">DevOps Assignees</label>
                  <select
                    multiple
                    value={editFormData.devopsAssignees || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setEditFormData({ ...editFormData, devopsAssignees: selected });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    size={5}
                  >
                    {availableDevops.map((d: any) => (
                      <option key={d._id || d.id} value={d._id || d.id}>
                        {d.username || d.name} - {d.email}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple DevOps assignees</p>
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={savingEdit}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingEdit ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeploymentCard;

